import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useAuth } from '@/context/AuthContext';
import { Loader2 } from 'lucide-react';
import { logDebug } from '@/utils/logger';

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

const AdminProtectedRoute = ({ children }: AdminProtectedRouteProps) => {
  const { isAdmin, isLoading, redirectToLogin } = useAdminAuth();
  const { user, userRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    logDebug('AdminProtectedRoute check', {
      component: 'AdminProtectedRoute',
      isLoading,
      isAdmin,
      metadata: { timestamp: new Date().toISOString() }
    });

    if (isLoading) return;

    if (isAdmin) {
      logDebug('Admin access granted', {
        component: 'AdminProtectedRoute',
        action: 'access_granted'
      });
      return;
    }

    // Authenticated but not admin → route to their own area to avoid
    // /login ↔ /admin redirect loops driven by stale postLoginRedirect.
    if (user && userRole && userRole !== 'admin') {
      localStorage.removeItem('postLoginRedirect');
      if (userRole === 'supplier') {
        navigate('/fornecedor/painel/inicio', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
      return;
    }

    logDebug('Access denied - redirecting to login', {
      component: 'AdminProtectedRoute',
      action: 'redirect',
      metadata: { reason: 'not_admin' }
    });
    redirectToLogin();
  }, [isLoading, isAdmin, user, userRole, redirectToLogin, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-lg">Verificando permissões de administrador...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Will redirect
  }

  return <>{children}</>;
};

export default AdminProtectedRoute;